import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useCVAnalysis from "../hooks/useCVAnalysis";

// Image - using userIcon.png from public directory

const Navbar = ({ onToggleSidebar, sidebarOpen, isMobile }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    const { user, signOut } = useAuth();
    const { getLatestAnalysis } = useCVAnalysis(user);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCVAnalyzerClick = async (e) => {
        e.preventDefault();
        if (user) {
            const latestAnalysis = await getLatestAnalysis();
            if (latestAnalysis && latestAnalysis.cv_analysis_results) {
                navigate(`/cv-analysis-results?id=${latestAnalysis.id}`);
            } else {
                navigate("/cv-analyser");
            }
        } else {
            navigate("/cv-analyser");
        }
    };

    const handleSectionClick = (sectionId) => {
        if (location.pathname === '/') {
            // If on landing page, scroll to section
            const element = document.getElementById(sectionId);
            if (element) {
                const navbarHeight = 80; // Approximate navbar height
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - navbarHeight;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        } else {
            // If not on landing page, navigate to landing page with hash
            navigate(`/#${sectionId}`);
            // After navigation, scroll to section
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const navbarHeight = 80; // Approximate navbar height
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - navbarHeight;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    };


    return (
        <nav
            className={`sticky top-0 z-30 ${
                location.pathname === "/" ? "bg-[#f9f9f9]" : "bg-white"
            }`}
        >
            <div className="max-w-full mx-auto px-6 py-4">
                <div className="flex justify-between items-center h-12">
                    <div className="flex items-center">
                        {/* Mobile Hamburger Menu Button - Only for logged-in users */}
                        {user && (
                            <button
                                className="md:hidden p-2 mr-2"
                                onClick={() => onToggleSidebar && onToggleSidebar()}
                            >
                                {isMobile && sidebarOpen ? (
                                    // Cross icon when sidebar is open
                                    <div className="w-6 h-6 flex items-center justify-center">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                ) : (
                                    // Hamburger icon when sidebar is closed
                                    <div className="w-6 h-5 flex flex-col justify-between">
                                        <span className="w-full h-0.5 bg-black"></span>
                                        <span className="w-full h-0.5 bg-black"></span>
                                        <span className="w-full h-0.5 bg-black"></span>
                                    </div>
                                )}
                            </button>
                        )}

                        {/* Logo for non-logged-in users */}
                        {!user && (
                            <div 
                                className="logo cursor-pointer"
                                onClick={() => navigate("/")}
                            >
                                Doctors' Jobs <span className="logo-badge">Beta</span>
                            </div>
                        )}
                    </div>

                    {/* Center Navigation for non-logged-in users */}
                    {!user && (
                        <div className="hidden md:flex flex-1 justify-center items-center space-x-8">
                            <button
                                onClick={() => handleSectionClick('job-applications-tracker')}
                                className="text-lg font-medium text-black relative group"
                            >
                                Job Application Tracker
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                            </button>
                            <button
                                onClick={() => handleSectionClick('mock-interview')}
                                className="text-lg font-medium text-black relative group"
                            >
                                Mock Interview
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                            </button>
                            <button
                                onClick={() => handleSectionClick('cv-analyser')}
                                className="text-lg font-medium text-black relative group"
                            >
                                CV Analyser
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                            </button>
                            <Link
                                to="/blogs"
                                className="text-lg font-medium text-black relative group"
                            >
                                Blog
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                            </Link>
                        </div>
                    )}

                    {/* Desktop Navigation for logged-in users - HIDDEN since we have sidebar navigation */}
                    {/* {user && (
                        <div className="hidden md:flex flex-1 justify-center">
                            <div className="flex space-x-8">
                                <Link
                                    to="/"
                                    className={`text-lg font-medium text-black hover:underline decoration-2 ${
                                        location.pathname === "/" && "underline"
                                    }`}
                                >
                                    Job Board
                                </Link>
                                <Link
                                    to="/job-applications-tracker"
                                    className={`text-lg font-medium text-black hover:underline decoration-2 ${
                                        location.pathname ===
                                            "/job-applications-tracker" &&
                                        "underline"
                                    }`}
                                >
                                    Job Applications Tracker
                                </Link>
                                <a
                                    href="#"
                                    onClick={handleCVAnalyzerClick}
                                    className={`text-lg font-medium text-black hover:underline decoration-2 ${
                                        (location.pathname === "/cv-analyser" ||
                                        location.pathname === "/cv-analysis-results") &&
                                        "underline"
                                    }`}
                                >
                                    CV Analyser
                                </a>
                            </div>
                        </div>
                    )} */}

                    <div className="flex items-center space-x-3">
                        {/* Mobile Auth Buttons */}
                        {user ? (
                            <div className="flex md:hidden items-center space-x-2">
                                {/* Notifications Icon */}
                                    <button className="rounded-full hover:bg-gray-100 transition p-2">
                                        <img src="/bell-1.svg" alt="Notifications bell icon" className="w-6 h-6" />
                                </button>
                                {/* Profile Icon with Dropdown */}
                                <div className="relative" ref={userMenuRef}>
                                    <button
                                        onClick={() => setShowUserMenu(!showUserMenu)}
                                        className="rounded-full bg-gray-200 hover:bg-gray-300 transition p-1"
                                    >
                                        <img src="/userIcon.png" alt="User profile avatar" className="w-6 h-6 rounded-full object-cover" />
                                    </button>
                                    
                                    {/* Dropdown Menu */}
                                    {showUserMenu && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                            <button
                                                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-t-lg transition"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('Account settings clicked on mobile');
                                                    setShowUserMenu(false);
                                                    navigate("/profile");
                                                }}
                                            >
                                                Account settings
                                            </button>
                                            <button
                                                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('Support button clicked on mobile');
                                                    setShowUserMenu(false);
                                                    window.open("https://tally.so/r/wb6krg", "_blank");
                                                }}
                                            >
                                                Support
                                            </button>
                                            <button
                                                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-b-lg transition"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('Logout button clicked on mobile');
                                                    setShowUserMenu(false);
                                                    signOut();
                                                }}
                                            >
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* Mobile Hamburger Menu Button */
                            <div className="flex md:hidden items-center">
                                <button
                                    className="p-2"
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                >
                                    <div className="w-6 h-5 flex flex-col justify-between">
                                        <span className={`w-full h-0.5 bg-black transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                                        <span className={`w-full h-0.5 bg-black transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                                        <span className={`w-full h-0.5 bg-black transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* Desktop Auth Buttons */}
                        <div className="hidden md:flex items-center space-x-3">
                            {user ? (
                                <>
                                    {/* Notifications Icon */}
                                    <button className="rounded-full hover:bg-gray-100 transition p-2">
                                        <img src="/bell-1.svg" alt="Notifications bell icon" className="w-8 h-8" />
                                    </button>
                                    {/* Profile Icon with Dropdown */}
                                    <div className="relative" ref={userMenuRef}>
                                        <button
                                            onClick={() => setShowUserMenu(!showUserMenu)}
                                            className="rounded-full bg-gray-200 hover:bg-gray-300 transition p-1"
                                        >
                                            <img src="/userIcon.png" alt="User profile avatar" className="w-8 h-8 rounded-full object-cover" />
                                        </button>
                                        
                                        {/* Dropdown Menu */}
                                        {showUserMenu && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                                <button
                                                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-t-lg transition"
                                                    onClick={() => {
                                                        setShowUserMenu(false);
                                                        navigate("/profile");
                                                    }}
                                                >
                                                    Account settings
                                                </button>
                                                <button
                                                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition"
                                                    onClick={() => {
                                                        setShowUserMenu(false);
                                                        window.open("https://tally.so/r/wb6krg", "_blank");
                                                    }}
                                                >
                                                    Support
                                                </button>
                                                <button
                                                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-b-lg transition"
                                                    onClick={() => {
                                                        setShowUserMenu(false);
                                                        signOut();
                                                    }}
                                                >
                                                    Logout
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className="px-6 py-2 rounded-full border border-black text-black bg-white font-medium text-sm hover:bg-gray-100 transition"
                                    >
                                        Sign In
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="px-6 py-2 rounded-full bg-black text-white font-medium text-sm hover:bg-black transition"
                                    >
                                        Sign up
                                    </Link>
                                </>
                            )}
                        </div>

                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                {isMenuOpen && (
                    <div className="fixed inset-0 bg-white md:hidden z-50">
                        <div className="flex flex-col h-full">
                            {/* Header with close button */}
                            <div className="flex justify-between items-center px-6 py-4 border-b">
                                <div className="logo cursor-pointer" onClick={() => navigate("/")}>
                                    Doctors' Jobs <span className="logo-badge">Beta</span>
                                </div>
                                <button
                                    className="p-2"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <div className="w-6 h-5 flex flex-col justify-between">
                                        <span className="w-full h-0.5 bg-black transform rotate-45 translate-y-2"></span>
                                        <span className="w-full h-0.5 bg-black transform -rotate-45 -translate-y-2"></span>
                                    </div>
                                </button>
                            </div>

                            {/* Menu Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="flex flex-col space-y-6">
                                    {user ? (
                                        <>
                                            <div className="space-y-4">
                                                <Link
                                                    to="/"
                                                    className={`block text-xl font-medium text-black hover:underline decoration-2 ${
                                                        location.pathname === "/" && "underline"
                                                    }`}
                                                    onClick={() => setIsMenuOpen(false)}
                                                >
                                                    Job Board
                                                </Link>
                                                <Link
                                                    to="/job-applications-tracker"
                                                    className={`block text-xl font-medium text-black hover:underline decoration-2 ${
                                                        location.pathname === "/job-applications-tracker" && "underline"
                                                    }`}
                                                    onClick={() => setIsMenuOpen(false)}
                                                >
                                                    Job Applications Tracker
                                                </Link>
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleCVAnalyzerClick(e);
                                                        setIsMenuOpen(false);
                                                    }}
                                                    className={`block text-xl font-medium text-black hover:underline decoration-2 ${
                                                        (location.pathname === "/cv-analyser" || location.pathname === "/cv-analysis-results") && "underline"
                                                    }`}
                                                >
                                                    CV Analyser
                                                </a>
                                            </div>
                                            <div className="pt-4 border-t">
                                                <button
                                                    className="px-6 py-2 rounded-full border border-black bg-black text-white font-medium text-sm hover:bg-black transition"
                                                    onClick={() => {
                                                        signOut();
                                                        setIsMenuOpen(false);
                                                    }}
                                                >
                                                    Logout
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-4">
                                                <button
                                                    onClick={() => {
                                                        handleSectionClick('job-applications-tracker');
                                                        setIsMenuOpen(false);
                                                    }}
                                                    className="block text-lg font-medium text-black relative group text-left w-full"
                                                >
                                                    Job Application Tracker
                                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleSectionClick('mock-interview');
                                                        setIsMenuOpen(false);
                                                    }}
                                                    className="block text-lg font-medium text-black relative group text-left w-full"
                                                >
                                                    Mock Interview
                                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleSectionClick('cv-analyser');
                                                        setIsMenuOpen(false);
                                                    }}
                                                    className="block text-lg font-medium text-black relative group text-left w-full"
                                                >
                                                    CV Analyser
                                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                                                </button>
                                                <Link
                                                    to="/blogs"
                                                    className="block text-lg font-medium text-black relative group text-left w-full"
                                                    onClick={() => setIsMenuOpen(false)}
                                                >
                                                    Blog
                                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
                                                </Link>
                                            </div>
                                            <div className="space-y-4 pt-4">
                                                <Link
                                                    to="/login"
                                                    className="block w-full px-6 py-3 rounded-full border border-black text-black bg-white font-medium text-sm hover:bg-gray-100 transition text-center"
                                                    onClick={() => setIsMenuOpen(false)}
                                                >
                                                    Sign In
                                                </Link>
                                                <Link
                                                    to="/signup"
                                                    className="block w-full px-6 py-3 rounded-full bg-black text-white font-medium text-sm hover:bg-black transition text-center"
                                                    onClick={() => setIsMenuOpen(false)}
                                                >
                                                    Sign up
                                                </Link>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </nav>
    );
};

export default Navbar;
